import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Eye, Pencil, AlertTriangle } from 'lucide-react';

// 30-minute time slots 6:00 AM – 9:30 PM
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 6; h <= 21; h++) {
    for (const m of [0, 30]) {
      if (h === 21 && m === 30) break;
      const period = h < 12 ? 'AM' : 'PM';
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      slots.push(`${hour12}:${m === 0 ? '00' : '30'} ${period}`);
    }
  }
  return slots;
})();

const formatDateForDisplay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const parseDateToIso = (display) => {
  if (!display) return '';
  const cleaned = display.replace(/^[A-Za-z]+,\s*/, '');
  const d = new Date(cleaned);
  if (isNaN(d)) return '';
  return d.toISOString().slice(0, 10);
};

// Intake lifecycle: draft → open → reviewing → closed
const STATUS_BADGE = {
  draft:      'bg-yellow-100 text-yellow-800',
  open:       'bg-green-100 text-green-800',
  reviewing:  'bg-blue-100 text-blue-800',
  closed:     'bg-gray-100 text-gray-600',
  // legacy aliases
  shortlisted: 'bg-blue-100 text-blue-800',
  review_closed: 'bg-blue-100 text-blue-800',
  aptitude_sent: 'bg-blue-100 text-blue-800',
  test_phase: 'bg-blue-100 text-blue-800',
  enrolling:  'bg-blue-100 text-blue-800',
  evaluation: 'bg-blue-100 text-blue-800',
  completed:  'bg-gray-100 text-gray-600',
};

const STATUS_LABEL = {
  draft:      'Draft',
  open:       'Open',
  reviewing:  'Reviewing',
  closed:     'Closed',
  // legacy aliases
  shortlisted: 'Reviewing',
  review_closed: 'Reviewing',
  aptitude_sent: 'Reviewing',
  test_phase: 'Reviewing',
  enrolling:  'Reviewing',
  evaluation: 'Reviewing',
  completed:  'Closed',
};

const DEFAULT_ELIGIBILITY = { max_age: 27, llb_min_completion_year: 2023, gpa_min_4: 2.7, gpa_min_5: 3.3 };
const DEFAULT_SCORING = { gpa_weight: 70, a_level_weight: 30, final_gpa_weight: 50, final_a_level_weight: 20, final_aptitude_weight: 30 };

const EMPTY_FORM = {
  name: '', description: '',
  applications_open_date: '', applications_close_date: '',
  target_size: '', capacity: '',
  aptitude_test_datetime: '', aptitude_test_date: '', aptitude_test_time: '',
  aptitude_test_duration_hours: '120',
  eligibility_criteria: { ...DEFAULT_ELIGIBILITY },
  scoring_config: { ...DEFAULT_SCORING },
};

export default function AdminIntakesPage() {
  const navigate = useNavigate();
  const [intakePeriods, setIntakePeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cohorts, setCohorts] = useState([]);

  // Create dialog
  const [newDialog, setNewDialog] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY_FORM);

  // Funnel
  const [funnelStats, setFunnelStats] = useState({});
  const [loadingFunnel, setLoadingFunnel] = useState(null);
  const [expandedFunnel, setExpandedFunnel] = useState(null);

  // Generic transition confirmation dialog
  const [transitionDialog, setTransitionDialog] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState(null); // { intake, toStatus, title, description }
  const [transitionStats, setTransitionStats] = useState(null);
  const [transitioning, setTransitioning] = useState(false);

  // Edit intake dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editingIntakeId, setEditingIntakeId] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Rollback confirmation dialog
  const [rollbackDialog, setRollbackDialog] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState(null); // { intake, toStatus }
  const [rollingBack, setRollingBack] = useState(false);

  // Bulk enroll dialog
  const [bulkEnrollDialog, setBulkEnrollDialog] = useState(false);
  const [selectedIntakeForEnroll, setSelectedIntakeForEnroll] = useState(null);
  const [enrollCohortId, setEnrollCohortId] = useState('');
  const [bulkEnrolling, setBulkEnrolling] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    fetchIntakes();
    fetchCohorts();
  }, []);

  const fetchIntakes = async () => {
    try {
      const res = await axios.get(`${API}/intake-periods`, auth());
      setIntakePeriods(res.data);
    } catch {
      toast.error('Failed to load intake periods');
    } finally {
      setLoading(false);
    }
  };

  const fetchCohorts = async () => {
    try {
      const res = await axios.get(`${API}/cohorts`, auth());
      setCohorts(res.data);
    } catch {}
  };

  const handleCreate = async () => {
    if (!newForm.name) { toast.error('Name is required'); return; }
    try {
      const payload = {
        name: newForm.name,
        description: newForm.description || undefined,
        applications_open_date: newForm.applications_open_date || undefined,
        applications_close_date: newForm.applications_close_date || undefined,
        target_size: newForm.target_size ? parseInt(newForm.target_size) : undefined,
        capacity: newForm.capacity ? parseInt(newForm.capacity) : undefined,
        aptitude_test_datetime: newForm.aptitude_test_datetime || undefined,
        aptitude_test_date: formatDateForDisplay(newForm.aptitude_test_date) || undefined,
        aptitude_test_time: newForm.aptitude_test_time || undefined,
        aptitude_test_duration_hours: newForm.aptitude_test_duration_hours ? parseFloat(newForm.aptitude_test_duration_hours) : 2,
        eligibility_criteria: newForm.eligibility_criteria,
        scoring_config: newForm.scoring_config,
      };
      await axios.post(`${API}/intake-periods`, payload, auth());
      toast.success('Intake period created');
      setNewDialog(false);
      setNewForm(EMPTY_FORM);
      fetchIntakes();
    } catch {
      toast.error('Failed to create intake period');
    }
  };

  const handleDelete = async (intakeId) => {
    if (!window.confirm('Delete this intake period? Applications linked to it will remain but lose their intake association.')) return;
    try {
      await axios.delete(`${API}/intake-periods/${intakeId}`, auth());
      toast.success('Intake period deleted');
      fetchIntakes();
    } catch {
      toast.error('Failed to delete intake period');
    }
  };

  const openEdit = (intake) => {
    setEditingIntakeId(intake.id);
    setEditForm({
      name: intake.name || '',
      description: intake.description || '',
      applications_open_date: intake.applications_open_date || '',
      applications_close_date: intake.applications_close_date || '',
      target_size: intake.target_size != null ? String(intake.target_size) : '',
      capacity: intake.capacity != null ? String(intake.capacity) : '',
      aptitude_test_datetime: intake.aptitude_test_datetime
        ? intake.aptitude_test_datetime.slice(0, 16)
        : '',
      aptitude_test_date: parseDateToIso(intake.aptitude_test_date),
      aptitude_test_time: intake.aptitude_test_time || '',
      aptitude_test_duration_hours: intake.aptitude_test_duration_hours != null
        ? String(intake.aptitude_test_duration_hours)
        : '2',
      eligibility_criteria: intake.eligibility_criteria || { ...DEFAULT_ELIGIBILITY },
      scoring_config: intake.scoring_config || { ...DEFAULT_SCORING },
    });
    setEditDialog(true);
  };

  const handleEdit = async () => {
    if (!editForm.name) { toast.error('Name is required'); return; }
    setEditSaving(true);
    try {
      const payload = {
        name: editForm.name,
        description: editForm.description || null,
        applications_open_date: editForm.applications_open_date || null,
        applications_close_date: editForm.applications_close_date || null,
        target_size: editForm.target_size ? parseInt(editForm.target_size) : null,
        capacity: editForm.capacity ? parseInt(editForm.capacity) : null,
        aptitude_test_datetime: editForm.aptitude_test_datetime || null,
        aptitude_test_date: formatDateForDisplay(editForm.aptitude_test_date) || null,
        aptitude_test_time: editForm.aptitude_test_time || null,
        aptitude_test_duration_hours: editForm.aptitude_test_duration_hours
          ? parseFloat(editForm.aptitude_test_duration_hours)
          : 2,
        eligibility_criteria: editForm.eligibility_criteria,
        scoring_config: editForm.scoring_config,
      };
      await axios.put(`${API}/intake-periods/${editingIntakeId}`, payload, auth());
      toast.success('Intake updated');
      setEditDialog(false);
      fetchIntakes();
    } catch {
      toast.error('Failed to update intake');
    } finally {
      setEditSaving(false);
    }
  };

  const RISKY_ROLLBACK_FROM = new Set(['reviewing', 'closed']);

  const openRollback = (intake, toStatus) => {
    setRollbackTarget({ intake, toStatus });
    setRollbackDialog(true);
  };

  const confirmRollback = async () => {
    if (!rollbackTarget) return;
    setRollingBack(true);
    try {
      await axios.patch(
        `${API}/intake-periods/${rollbackTarget.intake.id}/status?status=${rollbackTarget.toStatus}`,
        {}, auth()
      );
      toast.success(`Intake rolled back to ${STATUS_LABEL[rollbackTarget.toStatus] || rollbackTarget.toStatus}`);
      setRollbackDialog(false);
      fetchIntakes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to rollback status');
    } finally {
      setRollingBack(false);
    }
  };

  // Simple status changes that need no confirmation (draft→open, open→reviewing, aptitude_sent→enrolling)
  const handleSimpleStatus = async (intakeId, status) => {
    try {
      await axios.patch(`${API}/intake-periods/${intakeId}/status?status=${status}`, {}, auth());
      toast.success(`Intake marked as ${STATUS_LABEL[status] || status}`);
      fetchIntakes();
    } catch {
      toast.error('Failed to update status');
    }
  };

  // Open confirmation dialog — action can be a status change or an operation endpoint
  // transitionTarget: { intake, toStatus?, endpoint?, successMsg? }
  const openTransitionDialog = async (intake, opts) => {
    let stats = null;
    try {
      const res = await axios.get(`${API}/intake-periods/${intake.id}/stats`, auth());
      stats = res.data;
    } catch {}
    setTransitionStats(stats);
    setTransitionTarget({ intake, ...opts });
    setTransitionDialog(true);
  };

  const confirmTransition = async () => {
    if (!transitionTarget) return;
    setTransitioning(true);
    try {
      let data;
      if (transitionTarget.endpoint) {
        // Operational action (close-review, bulk-send-test)
        const res = await axios.post(transitionTarget.endpoint, {}, auth());
        data = res.data;
      } else {
        // Simple status change
        const res = await axios.patch(
          `${API}/intake-periods/${transitionTarget.intake.id}/status?status=${transitionTarget.toStatus}`,
          {}, auth()
        );
        data = res.data;
      }
      const msg = transitionTarget.successMsg
        ? transitionTarget.successMsg(data)
        : (data.message || 'Status updated');
      toast.success(msg);
      setTransitionDialog(false);
      fetchIntakes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally {
      setTransitioning(false);
    }
  };

  const openBulkEnroll = (intake) => {
    setSelectedIntakeForEnroll(intake);
    setEnrollCohortId(cohorts[0]?.id || '');
    setBulkEnrollDialog(true);
  };

  const handleBulkEnroll = async () => {
    if (!enrollCohortId) { toast.error('Select a cohort'); return; }
    setBulkEnrolling(true);
    try {
      const res = await axios.post(`${API}/intake-periods/${selectedIntakeForEnroll.id}/bulk-enroll?cohort_id=${enrollCohortId}`, {}, auth());
      toast.success(res.data.message);
      setBulkEnrollDialog(false);
    } catch {
      toast.error('Bulk enroll failed');
    } finally {
      setBulkEnrolling(false);
    }
  };

  const toggleFunnel = async (intakeId) => {
    if (expandedFunnel === intakeId) { setExpandedFunnel(null); return; }
    setExpandedFunnel(intakeId);
    if (funnelStats[intakeId]) return;
    setLoadingFunnel(intakeId);
    try {
      const res = await axios.get(`${API}/intake-periods/${intakeId}/stats`, auth());
      setFunnelStats(prev => ({ ...prev, [intakeId]: res.data }));
    } catch { toast.error('Failed to load funnel stats'); }
    finally { setLoadingFunnel(null); }
  };

  // Transition dialog content keyed by action name
  const transitionMeta = transitionTarget ? (transitionTarget.meta || null) : null;

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-playfair font-bold text-primary">Intake Periods</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Create and manage application cycles</p>
        </div>
        <Button onClick={() => setNewDialog(true)}>+ New Intake Period</Button>
      </div>

      <div className="p-6">
        <Card>
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Open</TableHead>
                    <TableHead>Close</TableHead>
                    <TableHead>Capacity</TableHead>
                    <TableHead>Test Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {intakePeriods.map((intake) => (
                    <React.Fragment key={intake.id}>
                      <TableRow className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/admin/intakes/${intake.id}`)}>
                        <TableCell className="font-medium text-primary hover:underline">{intake.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{intake.applications_open_date || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{intake.applications_close_date || '—'}</TableCell>
                        <TableCell className="text-sm">{intake.capacity ?? intake.target_size ?? '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {intake.aptitude_test_date
                            ? `${intake.aptitude_test_date}${intake.aptitude_test_time ? ' · ' + intake.aptitude_test_time : ''}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_BADGE[intake.status] || 'bg-gray-100 text-gray-600'}`}>
                            {STATUS_LABEL[intake.status] || intake.status}
                          </span>
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1.5 flex-wrap items-center">
                            <Button size="sm" variant="outline"
                              onClick={() => navigate('/admin/applications', { state: { intakeId: intake.id } })}>
                              <Eye className="w-3 h-3 mr-1" /> Apps
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => toggleFunnel(intake.id)}>
                              📊
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openEdit(intake)}>
                              <Pencil className="w-3 h-3" />
                            </Button>

                            {/* Forward stage buttons */}
                            {intake.status === 'draft' && (
                              <Button size="sm" onClick={() => handleSimpleStatus(intake.id, 'open')}>Open</Button>
                            )}
                            {intake.status === 'open' && (
                              <Button size="sm" variant="secondary"
                                onClick={() => handleSimpleStatus(intake.id, 'reviewing')}>
                                Start Review
                              </Button>
                            )}
                            {['reviewing', 'shortlisted', 'review_closed', 'aptitude_sent', 'test_phase', 'enrolling', 'evaluation'].includes(intake.status) && (
                              <>
                                <Button size="sm" className="bg-purple-700 hover:bg-purple-800 text-white"
                                  onClick={() => openTransitionDialog(intake, {
                                    endpoint: `${API}/intake-periods/${intake.id}/close-review`,
                                    meta: { title: 'Complete Review & Send Emails', description: 'Shortlisted applicants receive a shortlisted email; all others receive a decline email. This cannot be undone.', confirmLabel: 'Confirm & Send All Emails', confirmClass: 'bg-purple-700 hover:bg-purple-800 text-white' },
                                    successMsg: (d) => `Emails sent — ${d.shortlisted_emails_sent ?? 0} shortlisted, ${d.declined_emails_sent ?? 0} declined`,
                                  })}>
                                  Complete Review
                                </Button>
                                <Button size="sm" className="bg-indigo-700 hover:bg-indigo-800 text-white"
                                  onClick={() => openTransitionDialog(intake, {
                                    endpoint: `${API}/intake-periods/${intake.id}/bulk-send-test`,
                                    meta: { title: 'Send Academic Assessments', description: `Assessment links will be sent to all shortlisted applicants. Duration: ${intake.aptitude_test_duration_hours ?? 120} minutes. This cannot be undone.`, confirmLabel: 'Send All Assessment Links', confirmClass: 'bg-indigo-700 hover:bg-indigo-800 text-white' },
                                    successMsg: (d) => `Assessment links sent to ${d.test_links_sent ?? d.sent ?? 0} applicants`,
                                  })}>
                                  Send Assessments
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openBulkEnroll(intake)}>
                                  Bulk Enroll
                                </Button>
                                <Button size="sm" className="bg-gray-800 hover:bg-gray-900 text-white"
                                  onClick={() => handleSimpleStatus(intake.id, 'closed')}>
                                  Close Intake
                                </Button>
                              </>
                            )}

                            {/* Rollback buttons */}
                            {intake.status === 'open' && (
                              <Button size="sm" variant="ghost" className="text-xs text-muted-foreground"
                                onClick={() => openRollback(intake, 'draft')}>↩ Draft</Button>
                            )}
                            {['reviewing', 'shortlisted', 'review_closed', 'aptitude_sent', 'test_phase', 'enrolling', 'evaluation'].includes(intake.status) && (
                              <Button size="sm" variant="ghost" className="text-xs text-muted-foreground"
                                onClick={() => openRollback(intake, 'open')}>↩ Open</Button>
                            )}
                            {intake.status === 'closed' && (
                              <Button size="sm" variant="ghost" className="text-xs text-muted-foreground"
                                onClick={() => openRollback(intake, 'reviewing')}>↩ Reviewing</Button>
                            )}

                            <Button size="sm" variant="destructive" onClick={() => handleDelete(intake.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedFunnel === intake.id && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-gray-50 py-3 px-6">
                            {loadingFunnel === intake.id ? (
                              <p className="text-xs text-muted-foreground">Loading funnel…</p>
                            ) : funnelStats[intake.id] ? (
                              <FunnelBar stats={funnelStats[intake.id]} targetSize={intake.capacity ?? intake.target_size} />
                            ) : null}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                  {intakePeriods.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                        No intake periods yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Intake Dialog */}
      <Dialog open={newDialog} onOpenChange={setNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Intake Period</DialogTitle>
            <DialogDescription>Set up a new application cycle.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name *</Label>
              <Input placeholder="e.g. Intake 5 – Sept 2026" value={newForm.name}
                onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label>Description</Label>
              <Input placeholder="Optional short description" value={newForm.description}
                onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Applications Open</Label>
                <Input type="date" value={newForm.applications_open_date}
                  onChange={e => setNewForm(f => ({ ...f, applications_open_date: e.target.value }))} />
              </div>
              <div>
                <Label>Applications Close</Label>
                <Input type="date" value={newForm.applications_close_date}
                  onChange={e => setNewForm(f => ({ ...f, applications_close_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Size</Label>
                <Input type="number" placeholder="e.g. 30" value={newForm.target_size}
                  onChange={e => setNewForm(f => ({ ...f, target_size: e.target.value }))} />
              </div>
              <div>
                <Label>Enrollment Capacity</Label>
                <Input type="number" placeholder="Max seats" value={newForm.capacity}
                  onChange={e => setNewForm(f => ({ ...f, capacity: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assessment Date</Label>
                <Input type="date" value={newForm.aptitude_test_date}
                  onChange={e => setNewForm(f => ({ ...f, aptitude_test_date: e.target.value }))} />
              </div>
              <div>
                <Label>Assessment Time</Label>
                <select
                  value={newForm.aptitude_test_time}
                  onChange={e => setNewForm(f => ({ ...f, aptitude_test_time: e.target.value }))}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                >
                  <option value="">Select time</option>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">These appear in the shortlist email so applicants know when to expect the assessment.</p>
            <div>
              <Label>Test Duration (minutes)</Label>
              <Input type="number" min="1" max="480" step="1" placeholder="120" value={newForm.aptitude_test_duration_hours}
                onChange={e => setNewForm(f => ({ ...f, aptitude_test_duration_hours: e.target.value }))} />
              <p className="text-xs text-muted-foreground mt-1">How long applicants have to start the test after receiving the link. Link expires after this window.</p>
            </div>

            <div className="border-t pt-3">
              <p className="text-sm font-semibold mb-3">Eligibility Criteria</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Max Age</Label>
                  <Input type="number" value={newForm.eligibility_criteria.max_age}
                    onChange={e => setNewForm(f => ({ ...f, eligibility_criteria: { ...f.eligibility_criteria, max_age: parseInt(e.target.value) || 27 } }))} />
                </div>
                <div>
                  <Label>Min LLB Year</Label>
                  <Input type="number" value={newForm.eligibility_criteria.llb_min_completion_year}
                    onChange={e => setNewForm(f => ({ ...f, eligibility_criteria: { ...f.eligibility_criteria, llb_min_completion_year: parseInt(e.target.value) || 2023 } }))} />
                </div>
                <div>
                  <Label>Min GPA (4.0 scale)</Label>
                  <Input type="number" step="0.1" value={newForm.eligibility_criteria.gpa_min_4}
                    onChange={e => setNewForm(f => ({ ...f, eligibility_criteria: { ...f.eligibility_criteria, gpa_min_4: parseFloat(e.target.value) || 2.7 } }))} />
                </div>
                <div>
                  <Label>Min GPA (5.0 scale)</Label>
                  <Input type="number" step="0.1" value={newForm.eligibility_criteria.gpa_min_5}
                    onChange={e => setNewForm(f => ({ ...f, eligibility_criteria: { ...f.eligibility_criteria, gpa_min_5: parseFloat(e.target.value) || 3.3 } }))} />
                </div>
              </div>
            </div>

            <div className="border-t pt-3">
              <p className="text-sm font-semibold mb-3">Scoring Weights <span className="text-xs font-normal text-muted-foreground">(Application: GPA + A-Level = 100; Final: GPA + A-Level + Aptitude = 100)</span></p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>GPA Weight (application)</Label>
                  <Input type="number" value={newForm.scoring_config.gpa_weight}
                    onChange={e => setNewForm(f => ({ ...f, scoring_config: { ...f.scoring_config, gpa_weight: parseFloat(e.target.value) || 70 } }))} />
                </div>
                <div>
                  <Label>A-Level Weight (application)</Label>
                  <Input type="number" value={newForm.scoring_config.a_level_weight}
                    onChange={e => setNewForm(f => ({ ...f, scoring_config: { ...f.scoring_config, a_level_weight: parseFloat(e.target.value) || 30 } }))} />
                </div>
                <div>
                  <Label>GPA Weight (final)</Label>
                  <Input type="number" value={newForm.scoring_config.final_gpa_weight}
                    onChange={e => setNewForm(f => ({ ...f, scoring_config: { ...f.scoring_config, final_gpa_weight: parseFloat(e.target.value) || 50 } }))} />
                </div>
                <div>
                  <Label>A-Level Weight (final)</Label>
                  <Input type="number" value={newForm.scoring_config.final_a_level_weight}
                    onChange={e => setNewForm(f => ({ ...f, scoring_config: { ...f.scoring_config, final_a_level_weight: parseFloat(e.target.value) || 20 } }))} />
                </div>
                <div>
                  <Label>Aptitude Weight (final)</Label>
                  <Input type="number" value={newForm.scoring_config.final_aptitude_weight}
                    onChange={e => setNewForm(f => ({ ...f, scoring_config: { ...f.scoring_config, final_aptitude_weight: parseFloat(e.target.value) || 30 } }))} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create Intake</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transition Confirmation Dialog */}
      <Dialog open={transitionDialog} onOpenChange={setTransitionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{transitionMeta?.title}</DialogTitle>
            <DialogDescription>{transitionMeta?.description}</DialogDescription>
          </DialogHeader>

          {transitionStats && transitionTarget?.endpoint?.includes('close-review') && (
            <div className="grid grid-cols-3 gap-3 text-center my-2">
              <div className="bg-purple-50 p-3 rounded">
                <div className="text-2xl font-bold text-purple-700">{transitionStats.shortlisted ?? 0}</div>
                <div className="text-xs text-purple-600">Will receive shortlist email</div>
              </div>
              <div className="bg-red-50 p-3 rounded">
                <div className="text-2xl font-bold text-red-700">{transitionStats.declined ?? 0}</div>
                <div className="text-xs text-red-600">Will receive decline email</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <div className="text-2xl font-bold text-yellow-700">{transitionStats.pending_review ?? 0}</div>
                <div className="text-xs text-yellow-600">Unreviewed (→ declined)</div>
              </div>
            </div>
          )}

          {transitionStats && transitionTarget?.endpoint?.includes('bulk-send-test') && (
            <div className="bg-indigo-50 p-3 rounded text-center my-2">
              <div className="text-2xl font-bold text-indigo-700">{transitionStats.shortlisted_status ?? 0}</div>
              <div className="text-xs text-indigo-600">Shortlisted applicants will receive assessment links</div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setTransitionDialog(false)}>Cancel</Button>
            <Button className={transitionMeta?.confirmClass} onClick={confirmTransition} disabled={transitioning}>
              {transitioning ? 'Processing…' : transitionMeta?.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Intake Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Intake Period</DialogTitle>
            <DialogDescription>Update the details for this intake.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name *</Label>
              <Input value={editForm.name}
                onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={editForm.description}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Applications Open</Label>
                <Input type="date" value={editForm.applications_open_date}
                  onChange={e => setEditForm(f => ({ ...f, applications_open_date: e.target.value }))} />
              </div>
              <div>
                <Label>Applications Close</Label>
                <Input type="date" value={editForm.applications_close_date}
                  onChange={e => setEditForm(f => ({ ...f, applications_close_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Size</Label>
                <Input type="number" value={editForm.target_size}
                  onChange={e => setEditForm(f => ({ ...f, target_size: e.target.value }))} />
              </div>
              <div>
                <Label>Enrollment Capacity</Label>
                <Input type="number" value={editForm.capacity}
                  onChange={e => setEditForm(f => ({ ...f, capacity: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assessment Date</Label>
                <Input type="date" value={editForm.aptitude_test_date}
                  onChange={e => setEditForm(f => ({ ...f, aptitude_test_date: e.target.value }))} />
              </div>
              <div>
                <Label>Assessment Time</Label>
                <select
                  value={editForm.aptitude_test_time}
                  onChange={e => setEditForm(f => ({ ...f, aptitude_test_time: e.target.value }))}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                >
                  <option value="">Select time</option>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Test Duration (minutes)</Label>
              <Input type="number" min="1" max="480" step="1" value={editForm.aptitude_test_duration_hours}
                onChange={e => setEditForm(f => ({ ...f, aptitude_test_duration_hours: e.target.value }))} />
            </div>

            <div className="border-t pt-3">
              <p className="text-sm font-semibold mb-3">Eligibility Criteria</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Max Age</Label>
                  <Input type="number" value={editForm.eligibility_criteria?.max_age ?? 27}
                    onChange={e => setEditForm(f => ({ ...f, eligibility_criteria: { ...f.eligibility_criteria, max_age: parseInt(e.target.value) || 27 } }))} />
                </div>
                <div>
                  <Label>Min LLB Year</Label>
                  <Input type="number" value={editForm.eligibility_criteria?.llb_min_completion_year ?? 2023}
                    onChange={e => setEditForm(f => ({ ...f, eligibility_criteria: { ...f.eligibility_criteria, llb_min_completion_year: parseInt(e.target.value) || 2023 } }))} />
                </div>
                <div>
                  <Label>Min GPA (4.0 scale)</Label>
                  <Input type="number" step="0.1" value={editForm.eligibility_criteria?.gpa_min_4 ?? 2.7}
                    onChange={e => setEditForm(f => ({ ...f, eligibility_criteria: { ...f.eligibility_criteria, gpa_min_4: parseFloat(e.target.value) || 2.7 } }))} />
                </div>
                <div>
                  <Label>Min GPA (5.0 scale)</Label>
                  <Input type="number" step="0.1" value={editForm.eligibility_criteria?.gpa_min_5 ?? 3.3}
                    onChange={e => setEditForm(f => ({ ...f, eligibility_criteria: { ...f.eligibility_criteria, gpa_min_5: parseFloat(e.target.value) || 3.3 } }))} />
                </div>
              </div>
            </div>

            <div className="border-t pt-3">
              <p className="text-sm font-semibold mb-3">Scoring Weights <span className="text-xs font-normal text-muted-foreground">(Application: GPA + A-Level = 100; Final: GPA + A-Level + Aptitude = 100)</span></p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>GPA Weight (application)</Label>
                  <Input type="number" value={editForm.scoring_config?.gpa_weight ?? 70}
                    onChange={e => setEditForm(f => ({ ...f, scoring_config: { ...f.scoring_config, gpa_weight: parseFloat(e.target.value) || 70 } }))} />
                </div>
                <div>
                  <Label>A-Level Weight (application)</Label>
                  <Input type="number" value={editForm.scoring_config?.a_level_weight ?? 30}
                    onChange={e => setEditForm(f => ({ ...f, scoring_config: { ...f.scoring_config, a_level_weight: parseFloat(e.target.value) || 30 } }))} />
                </div>
                <div>
                  <Label>GPA Weight (final)</Label>
                  <Input type="number" value={editForm.scoring_config?.final_gpa_weight ?? 50}
                    onChange={e => setEditForm(f => ({ ...f, scoring_config: { ...f.scoring_config, final_gpa_weight: parseFloat(e.target.value) || 50 } }))} />
                </div>
                <div>
                  <Label>A-Level Weight (final)</Label>
                  <Input type="number" value={editForm.scoring_config?.final_a_level_weight ?? 20}
                    onChange={e => setEditForm(f => ({ ...f, scoring_config: { ...f.scoring_config, final_a_level_weight: parseFloat(e.target.value) || 20 } }))} />
                </div>
                <div>
                  <Label>Aptitude Weight (final)</Label>
                  <Input type="number" value={editForm.scoring_config?.final_aptitude_weight ?? 30}
                    onChange={e => setEditForm(f => ({ ...f, scoring_config: { ...f.scoring_config, final_aptitude_weight: parseFloat(e.target.value) || 30 } }))} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={editSaving}>
              {editSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback Confirmation Dialog */}
      <Dialog open={rollbackDialog} onOpenChange={setRollbackDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {rollbackTarget && RISKY_ROLLBACK_FROM.has(rollbackTarget.intake.status) && (
                <AlertTriangle className="w-5 h-5 text-orange-500" />
              )}
              Roll Back Intake Status
            </DialogTitle>
            <DialogDescription>
              {rollbackTarget && RISKY_ROLLBACK_FROM.has(rollbackTarget.intake.status)
                ? `Warning: emails have already been sent to applicants at the "${STATUS_LABEL[rollbackTarget.intake.status]}" stage. Rolling back will not unsend those emails. Continue?`
                : `Roll back "${rollbackTarget?.intake.name}" from ${STATUS_LABEL[rollbackTarget?.intake.status]} to ${STATUS_LABEL[rollbackTarget?.toStatus]}?`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackDialog(false)}>Cancel</Button>
            <Button
              className={rollbackTarget && RISKY_ROLLBACK_FROM.has(rollbackTarget.intake.status)
                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                : ''}
              onClick={confirmRollback}
              disabled={rollingBack}
            >
              {rollingBack ? 'Rolling back…' : 'Confirm Rollback'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Enroll Dialog */}
      <Dialog open={bulkEnrollDialog} onOpenChange={setBulkEnrollDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Bulk Enroll — {selectedIntakeForEnroll?.name}</DialogTitle>
            <DialogDescription>
              Enroll all shortlisted test-takers from this intake into a cohort. Already-enrolled applicants are skipped.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>Select Cohort</Label>
            <select
              className="w-full border rounded px-3 py-2 text-sm mt-1"
              value={enrollCohortId}
              onChange={e => setEnrollCohortId(e.target.value)}
            >
              <option value="">— Select cohort —</option>
              {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEnrollDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkEnroll} disabled={bulkEnrolling || !enrollCohortId}>
              {bulkEnrolling ? 'Enrolling…' : 'Enroll All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FunnelBar({ stats, targetSize }) {
  const stages = [
    { label: 'Applied',      value: stats.total,                  color: 'bg-blue-500' },
    { label: 'Shortlisted',  value: stats.shortlisted ?? 0,       color: 'bg-purple-500' },
    { label: 'Tests Sent',   value: stats.test_sent ?? 0,         color: 'bg-indigo-500' },
    { label: 'Test Done',    value: stats.test_completed ?? 0,    color: 'bg-sky-500' },
    { label: 'Enrolled',     value: stats.enrolled ?? 0,          color: 'bg-teal-500' },
  ];
  const max = stats.total || 1;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Conversion Funnel</p>
        {targetSize && <p className="text-xs text-muted-foreground">Capacity: {targetSize}</p>}
      </div>
      {stages.map(({ label, value, color }) => (
        <div key={label} className="flex items-center gap-3">
          <span className="text-xs w-24 text-right text-muted-foreground">{label}</span>
          <div className="flex-1 h-5 bg-gray-200 rounded overflow-hidden">
            <div className={`h-full ${color} rounded transition-all`} style={{ width: `${(value / max) * 100}%` }} />
          </div>
          <span className="text-xs w-8 font-semibold">{value ?? 0}</span>
          <span className="text-xs text-muted-foreground w-12">{max > 0 ? ((value / max) * 100).toFixed(0) : 0}%</span>
        </div>
      ))}
    </div>
  );
}
